<template>
  <bimini-default-section :section="section" :menu="menu" menu-item-component="wine-list-item"
                          class="menu--section-wine_list"></bimini-default-section>
</template>

<script lang="ts">
/**
 * Custom component for Romanza Wines by the Glass section.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.5.0
 * @copyright  Copyright (C) 2022-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2022-10-07 (1.0.0) (jhoward)
 *      - Copy from Romanza_wineList.vue component to Bimini_wineList.vue component.
 *
 *    2023-04-17 (1.1.0) (jhoward)
 *      - <Bimini_wineList> component uses TypeScript.
 *      - <Bimini_wineList> section prop is required.
 *      - <Bimini_wineList> component sets a blank array for pricing types when displaying items grouped by price.
 *      - Remove unused methods from <Bimini_wineList>.
 *
 *    2023-06-19 (1.2.0) (jhoward)
 *      - <Bimini_wineList> supports section.ms_show_pricing_header.
 *
 *    2023-06-21 (1.2.1) (jhoward)
 *      - Default to 1 pricing type rather than 0 in <Bimini_wineList>.
 *
 *  2023-08-23 (1.3.0) (jhoward)
 *    - Use <menu-version> in <Bimini_wineList>.
 *
 *  2025-02-11 (1.4.0) (jhoward)
 *    - Add <menu-schedules> to the <Bimini_wineList> component if in admin view.
 *
 *  2025-04-08 (1.5.0) (jhoward)
 *    - Use <default-textual-section> and <default-visual-section> in <Bimini_wineList>.
 *    - Add textual and visual menu CSS tweaks to <Bimini_wineList>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import BiminiDefaultSection from "./Bimini_defaultSection.vue";

export default defineComponent({
  components: {
    BiminiDefaultSection,
  },
  name: "Bimini_wineList",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: '',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
})
</script>

<style scoped>
.menu--section-wine_list:deep(.menu--section_title),
.menu--section-wine_list:deep(.menu--section_subtitle),
.menu--section-wine_list:deep(.menu--section_subgroup_title) {
  color: #3e2603;
  margin: 2rem auto 0.2em auto;
  padding: 0;
  text-align: center;
}

@media (prefers-contrast: more) {
  .menu--section-wine_list:deep(.menu--section_title),
  .menu--section-wine_list:deep(.menu--section_subtitle),
  .menu--section-wine_list:deep(.menu--section_subgroup_title) {
    color: #000;
  }
}

.menu--section-wine_list:deep(.menu--section_title),
.menu--section-wine_list:deep(.menu--section_subgroup_title span:first-child) {
  font-family: bodoni-urw, serif;
  font-weight: 700;
  font-style: normal;
  font-variant: small-caps;
  letter-spacing: 0.05em;
  font-size: 2em;
}

.menu--section-wine_list:deep(.menu--section_subtitle) {
  margin-top: -1em;
  font-size: 1em;
  font-weight: 400;
  margin-bottom: 0.4em;
}

.textual_menu--section.menu--section-wine_list:deep(.menu--section_subtitle) {
  margin-top: -0.3em;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title) {
  text-align: left;
  position: relative;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title::after) {
  content: '';
  position: absolute;
  bottom: 0;
  left: -3em;
  right: -3em;
  height: 0.06em;
  background-color: currentColor;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title span:first-child) {
  font-size: 1.5em;
}

.menu--section-wine_list:deep(.menu--section-group_by_price .menu--section_subgroup_title) {
  border-bottom: none;
  color: #000000;
  font-size: 0.8em;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title) {
  align-items: end;
}

.menu--section-wine_list:deep(.menu--section_subgroup_title span:not(:first-child)) {
  text-align: right;
  color: #000000;
  padding-bottom: 0.1em;
}

.menu--section-wine_list:deep(.menu--item + .menu--item) {
  padding-top: 0.15em;
}

.menu--section-wine_list:deep(.visual_menu--page_section-bottom) {
  text-align: center;
}

.menu--section-wine_list:deep(.menu--item_price) {
  text-align: right;
}

.menu--section.menu--section-wine_list:deep(.menu--item_subgroup) {
  grid-gap: 0;
}
</style>